/**
 * @file src/context/AuthContext.jsx
 * @description Global authentication state and user session management
 * 
 * Provides the authenticated user object to all components via React Context.
 * This is the single source of truth for:
 * - Current logged-in user
 * - User profile (displayName, email, photoURL, phone, settings)
 * - Auth state (loading, error)
 * - Auth methods (signup, login, logout, password reset, 2FA)
 * 
 * Usage in components:
 *   const { user, signup, login, logout } = useAuth();
 *   if (!user) return <LoginPage />;  // Protected route pattern
 * 
 * Features:
 * - Persists across page refreshes (Firebase restores session)
 * - Auto-syncs user profile from Firestore
 * - Generates and stores encryption key on first login
 * - Handles email verification and password reset flows
 * - Manages 2FA/MFA enrollment and verification
 * - Social login via Google OAuth
 * 
 * @dependencies
 *   - Firebase Auth (signup, login, session management)
 *   - Firestore (user profile storage and sync)
 *   - Cloud Functions (custom claims, admin checks)
 * 
 * @exports
 *   - AuthContext (context object)
 *   - AuthProvider (component wrapper)
 *   - useAuth (custom hook to access context)
 * 
 * @example
 *   // In App.jsx
 *   <AuthProvider>
 *     <YourApp />
 *   </AuthProvider>
 *   
 *   // In any component
 *   const { user, login } = useAuth();
 *   await login(email, password);
 */

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth } from "../firebase/auth";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  sendPasswordResetEmail,
} from "firebase/auth";
import { functions, httpsCallable } from "../firebase/functions";
import { doc, getDoc, setDoc } from "firebase/firestore";
import db from "../firebase/db";
import { getOrCreateKey, hasKey, exportKey, importKeyFromBase64 } from "../services/crypto";
import { PLAN_LIMITS } from "../data/planLimits";
import {
  getUserProfile,
  initializeUserProfile,
  updateUserProfile,
} from "../services/repository/userRepository";

const AuthContext = createContext();

// ── Cross-device key sync helpers ─────────────────────────────────────────────

/**
 * Push the locally-stored encryption key to the user's Firestore document
 * so other devices can retrieve it on login.
 */
async function syncKeyToFirestore(uid) {
  const b64 = await exportKey(uid);
  await setDoc(doc(db, 'users', uid), { encryptionKey: b64 }, { merge: true });
}

/**
 * Load the encryption key for a user, syncing across devices via Firestore:
 *  1. If the key is already in localStorage (same device), use it and
 *     fire-and-forget a sync to Firestore.
 *  2. If not in localStorage (new device), fetch from Firestore and cache locally.
 *  3. If nowhere (first ever login), generate a new key and store in both places.
 */
async function loadOrSyncEncryptionKey(uid) {
  if (hasKey(uid)) {
    const key = await getOrCreateKey(uid);
    // Best-effort: keep Firestore up to date for other devices
    syncKeyToFirestore(uid).catch((e) => console.warn('[AuthContext] Key sync skipped:', e));
    return key;
  }
  // New device: try to fetch the key from Firestore
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const remoteB64 = snap.data()?.encryptionKey;
    if (remoteB64) {
      await importKeyFromBase64(uid, remoteB64);
      console.log('[AuthContext] Encryption key loaded from Firestore (cross-device sync)');
      return getOrCreateKey(uid);
    }
  } catch (fetchErr) {
    console.error('[AuthContext] Could not fetch remote key:', fetchErr);
  }
  // First time ever: generate key, persist locally and remotely
  const key = await getOrCreateKey(uid);
  await syncKeyToFirestore(uid);
  return key;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("free");
  const [capsuleLimit, setCapsuleLimit] = useState(1);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cryptoKey, setCryptoKey] = useState(null);

  const isPremium = plan === "legacy";
  const isStarter = plan === "starter";

  const limits = {
    ...PLAN_LIMITS[plan],
    capsules: capsuleLimit,
  };

  const loadPlan = useCallback(async (currentUser) => {
    try {
      const data = await getUserProfile(currentUser.uid);
      const userPlan = data.plan || (data.isPremium ? "legacy" : "free");
      setPlan(userPlan);
      setSubscriptionStatus(data.subscriptionStatus || null);
      const stored = data.capsuleLimit;
      if (stored === null || stored === undefined) {
        setCapsuleLimit(userPlan === "legacy" ? Infinity : PLAN_LIMITS[userPlan].capsules);
      } else {
        setCapsuleLimit(stored);
      }
      return data;
    } catch {
      setPlan("free");
      setCapsuleLimit(1);
      return {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileData = await loadPlan(currentUser);
        // Ensure email + createdAt are persisted — Google OAuth users may not have them
        if (!profileData.createdAt || !profileData.email) {
          try {
            await initializeUserProfile(currentUser.uid, {
              ...(profileData.createdAt ? {} : { createdAt: new Date().toISOString() }),
              ...(profileData.email ? {} : { email: currentUser.email }),
              ...(profileData.displayName ? {} : { displayName: currentUser.displayName || '' }),
            });
          } catch (profileErr) {
            console.warn('[AuthContext] Could not persist profile fields:', profileErr);
          }
        }
        try {
          const key = await loadOrSyncEncryptionKey(currentUser.uid);
          setCryptoKey(key);
        } catch (keyErr) {
          console.error('[AuthContext] Failed to load encryption key:', keyErr);
          setCryptoKey(null);
        }
      } else {
        setPlan("free");
        setCapsuleLimit(1);
        setCryptoKey(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [loadPlan]);

  // Refresh local user object + plan (call after profile edits)
  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return {};
    await auth.currentUser.reload();
    setUser({ ...auth.currentUser });
    const data = await loadPlan(auth.currentUser);
    return data;
  }, [loadPlan]);

  const login = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };

  const loginWithEmail = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async ({ firstName, lastName, email, password }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: `${firstName} ${lastName}`.trim() });
    await initializeUserProfile(cred.user.uid, {
      displayName: `${firstName} ${lastName}`.trim(),
      email,
      plan: "free",
      capsuleLimit: 1,
      createdAt: new Date().toISOString(),
    });
    
    // Send branded verification email via Cloud Function (uses Resend + our template)
    // This replaces Firebase's default "Legacy Vault" email
    try {
      const sendVerificationEmail = httpsCallable(functions, "sendVerificationEmail");
      await sendVerificationEmail({});
    } catch (emailErr) {
      // Email send failed — user can manually request from /verify-email
      console.warn("Could not send verification email:", emailErr.message);
    }

    return cred.user;
  };

  // Update display name in Firebase Auth + Firestore
  const updateDisplayName = async (displayName) => {
    if (!auth.currentUser) return;
    await updateProfile(auth.currentUser, { displayName });
    await updateUserProfile(auth.currentUser.uid, { displayName });
    await refreshUser();
  };

  // Update phone + any other profile fields in Firestore only
  const updateProfileData = async (data) => {
    if (!auth.currentUser) return;
    await updateUserProfile(auth.currentUser.uid, data);
  };

  // SMS multi-factor enrollment helpers
  const enrollSmsStart = async (phoneNumber, recaptchaVerifier) => {
    if (!auth.currentUser) throw new Error("Not authenticated");
    const session = await multiFactor(auth.currentUser).getSession();
    const provider = new PhoneAuthProvider(auth);
    const verificationId = await provider.verifyPhoneNumber({ phoneNumber, session }, recaptchaVerifier);
    return verificationId;
  };

  const enrollSmsFinalize = async (verificationId, code) => {
    if (!auth.currentUser) throw new Error("Not authenticated");
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    await multiFactor(auth.currentUser).enroll(assertion, 'Phone');
    await refreshUser();
  };

  const unenrollMfa = async () => {
    if (!auth.currentUser) throw new Error("Not authenticated");
    const mfaUser = multiFactor(auth.currentUser);
    const enrolled = mfaUser.enrolledFactors;
    if (enrolled.length === 0) return;
    await mfaUser.unenroll(enrolled[0]);
    await refreshUser();
  };

  const hasMfa = !!(auth.currentUser && multiFactor(auth.currentUser).enrolledFactors.length > 0);

  const logout = async () => {
    await signOut(auth);
  };

  const sendPasswordReset = async (email) => {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth-action`,
      handleCodeInApp: true,
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  };

  const deleteAccount = async () => {
    if (!auth.currentUser) throw new Error("Not authenticated");
    const fn = httpsCallable(functions, "deleteAccount");
    await fn({});
    // Local state cleanup — auth state observer will fire but user is already gone
    setUser(null);
    setPlan("free");
    setCapsuleLimit(1);
    setCryptoKey(null);
    await signOut(auth).catch(() => {});
  };

  const isDemoMode = user?.isAnonymous === true;

  return (
    <AuthContext.Provider value={{
      user, loading, plan, isPremium, isStarter, capsuleLimit, subscriptionStatus, limits, cryptoKey,
      isDemoMode,
      login, loginWithEmail, signUpWithEmail, logout,
      refreshUser, updateDisplayName, updateProfileData,
      enrollSmsStart, enrollSmsFinalize, unenrollMfa, hasMfa,
      sendPasswordReset, deleteAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
